import React, { useCallback, useMemo } from 'react';
import { TopNav4 } from '@atlassian/jira-atlassian-navigation-nav4/src/index.tsx';
import { NAVIGATION_TOP_MARKS } from '@atlassian/jira-atlassian-navigation-performance-metrics/src/constants.tsx';
import {
	NavTopPerformanceMark,
	NavTopPerformanceReport,
	useNavPerformanceMetrics,
} from '@atlassian/jira-atlassian-navigation-performance-metrics/src/index.tsx';
import { recommendations } from '@atlassian/jira-atlassian-navigation-recommendations/src/ui/index.tsx';
import { RecommendationLoader } from '@atlassian/jira-atlassian-navigation-recommendations/src/ui/recommendation-loader/index.tsx';
import AsyncCustomThemeAdminFlag from '@atlassian/jira-custom-theme-admin-flag/src/async.tsx';
import { FavoritesContainer } from '@atlassian/jira-favorites-store/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import killswitch from '@atlassian/jira-killswitch/src/index.tsx';
import {
	MobileWebExperimentExposure,
	isMobileAndInMvpOrExistingUsersExperiment,
	MobileWebExperimentViewPanelNewTenantsExposure,
	MobileWebExperimentViewPanelExistingUserExposure,
	isMobileAndInViewPanelIterationExperiment,
} from '@atlassian/jira-mobile-web/src/index.tsx';
import { EditionAwareness } from '@atlassian/jira-navigation-apps-sidebar-edition-awareness/src/ui/index.tsx';
import { AppSwitcher as AppSwitcherNew } from '@atlassian/jira-navigation-apps-topnav4-app-switcher/src/ui/index.tsx';
import AsyncNav4RolloutAnalytics from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-analytics/src/async.tsx';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import { useCurrentRoute } from '@atlassian/jira-platform-router-utils/src/index.tsx';
import { useProjectContext } from '@atlassian/jira-providers-project-context/src/index.tsx';
import { RecentsContainer } from '@atlassian/jira-recents-store/src/index.tsx';
import { useApplicationPermissions } from '@atlassian/jira-tenant-context-controller/src/components/application-permissions/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useIsAnonymous } from '@atlassian/jira-tenant-context-controller/src/components/is-anonymous/index.tsx';
import { useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-site-admin/index.tsx';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { NavigationSuccessTrackerForNav3 } from '../controllers/navigation-success-tracker/main.tsx';
import { useTheme } from '../controllers/theme/main.tsx';
import { TopNav3Async } from './top-nav/async.tsx';
import { AppSwitcher as AppSwitcherOld } from './app-switcher/main.tsx';
import { ConversationAssistant } from './conversation-assistant/index.tsx';
import { Create } from './create/main.tsx';
import { Help } from './help/main.tsx';
import { InitialDataLoader } from './initial-data-loader/index.tsx';
import messages from './messages.tsx';
import { Notifications } from './notifications/main.tsx';
import { ProductHome } from './product-home/main.tsx';
import { Profile } from './profile/main.tsx';
import { NavigationRouterListener } from './router-listener/index.tsx';
import { Search } from './search/index.tsx';
import { Settings } from './settings/index.tsx';
import { SignIn } from './sign-in/main.tsx';
import { Nav4NotificationsData } from './notifications/Nav4NotificationsData.tsx';

const isSPA = () => {
	if (__SERVER__) return false;
	if (typeof window !== 'undefined') return window.__SPA__;
	return false;
};

const TopNavComponent = componentWithCondition(getWillShowNav4, Nav4TopNav, Nav3TopNav);

export function AtlassianNavigation() {
	return (
		<>
			<TopNavComponent />
			{/* AsyncNav4RolloutAnalytics fire analytics for both Nav3 and Nav4 */}
			{fg('jira_nav4') && <AsyncNav4RolloutAnalytics />}
			{fg('jira_mobile_web_mvp_2_feature_gate') && <MobileWebExperimentExposure />}
			{fg('jira_mobile_web_mvp_view_panel_kill_switch') && (
				<MobileWebExperimentViewPanelNewTenantsExposure />
			)}
			{fg('jira_mobile_web_mvp_view_panel_kill_switch') && (
				<MobileWebExperimentViewPanelExistingUserExposure />
			)}
		</>
	);
}

function Nav4TopNav() {
	const theme = useTheme();
	const { formatMessage } = useIntl();
	const isSiteAdmin = useIsSiteAdmin();
	const isMobileExperience = isMobileAndInMvpOrExistingUsersExperiment();

	const nav4NavigationProps = useMemo(
		() =>
			isMobileExperience
				? {
						hideSideNavToggleButton: true,
						renderSearch: isMobileAndInViewPanelIterationExperiment() ? Search : undefined,
						renderNotifications: isMobileAndInViewPanelIterationExperiment()
							? Notifications
							: undefined,
					}
				: {
						renderAppSwitcher: Nav4AppSwitcherMaybe,
						renderSearch: Search,
						renderEditionAwareness: EditionAwareness,
						renderHelp: Help,
						renderNotifications: Notifications,
						renderConversationAssistant: ConversationAssistant,
						renderSettings: Settings,
						renderProfile: Profile,
						renderSignIn: SignIn,
					},
		[isMobileExperience],
	);

	return (
		<>
			<TopNav4
				renderProductHome={ProductHome}
				renderCreate={Create}
				renderNav4Notifications={Nav4NotificationsData}
				{...nav4NavigationProps}
				theme={theme}
				label={formatMessage(messages.topNav)}
			/>
			<NavigationRouterListener />
			{isSiteAdmin && <AsyncCustomThemeAdminFlag />}
		</>
	);
}

function Nav3TopNav() {
	const topNavSegment = useNavPerformanceMetrics();
	topNavSegment.mark(NAVIGATION_TOP_MARKS.renderStart);

	const cloudId = useCloudId();
	const applicationPermissions = useApplicationPermissions();
	const isAnonymous = useIsAnonymous();
	const isSiteAdmin = useIsSiteAdmin();

	const route = useCurrentRoute();

	const renderNavigation = useCallback(
		() => (
			<>
				<TopNav3Async />
				{!__SERVER__ && (
					<>
						{!isAnonymous && !killswitch('navigation.disable.prefetched.data.for.dropdowns') && (
							<>
								<InitialDataLoader />
								<RecommendationLoader recommendations={recommendations} />
							</>
						)}
					</>
				)}
			</>
		),
		[isAnonymous],
	);

	const routeName = route.name ?? route.group ?? 'UNKNOWN';
	const projectContext = useProjectContext();
	const customData = useMemo(() => {
		return {
			route: routeName,
			projectType: projectContext?.data?.projectType ?? 'UNKNOWN',
			isSPA: isSPA(),
		};
	}, [routeName, projectContext]);

	return (
		<>
			<NavigationSuccessTrackerForNav3>
				{isAnonymous ? (
					renderNavigation()
				) : (
					<RecentsContainer
						isGlobal
						cloudId={cloudId}
						applicationPermissions={applicationPermissions}
					>
						<FavoritesContainer isGlobal applicationPermissions={applicationPermissions}>
							{renderNavigation()}
						</FavoritesContainer>
					</RecentsContainer>
				)}
			</NavigationSuccessTrackerForNav3>
			<NavigationRouterListener />
			<NavTopPerformanceMark metricKey={NAVIGATION_TOP_MARKS.renderEnd} />
			<NavTopPerformanceReport customData={customData} />
			{isSiteAdmin && <AsyncCustomThemeAdminFlag />}
		</>
	);
}

const Nav4AppSwitcherMaybe = componentWithFG(
	'blu-6347-app-switcher-ep',
	AppSwitcherNew,
	AppSwitcherOld,
);
